import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Supported credential types (mirrors backend CredentialType enum).
 */
export const CredentialTypeSchema = z.enum([
  "password",
  "totp",
  "auth_state",
  "api_token",
  "oauth_client",
  "certificate",
]);

/**
 * Summary of a credential (secret value redacted).
 */
export const CredentialSummarySchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: CredentialTypeSchema,
  /** For Password type: masked username. Null otherwise. */
  usernameMasked: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  lastUsedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * Create a new credential.
 */
export const CreateCredentialInputSchema = z.object({
  organizationId: z.string().uuid(),
  name: z
    .string()
    .min(1, "Name is required")
    .max(255, "Name must not exceed 255 characters"),
  type: CredentialTypeSchema,
  /** The secret value (password, token, etc.) — never returned by the API */
  secret: z.string().min(1, "Secret is required"),
  /** Type-specific metadata (e.g. { username } for Password) */
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Update an existing credential.
 */
export const UpdateCredentialInputSchema = z.object({
  organizationId: z.string().uuid(),
  credentialId: z.string().uuid(),
  name: z.string().min(1).max(255).optional(),
  /** If provided, replaces the stored secret */
  secret: z.string().min(1).optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

/**
 * Reference a single credential.
 */
export const CredentialByIdInputSchema = z.object({
  organizationId: z.string().uuid(),
  credentialId: z.string().uuid(),
});

/**
 * List credentials with optional type filter.
 */
export const ListCredentialsInputSchema = z.object({
  organizationId: z.string().uuid(),
  type: CredentialTypeSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});

export const ListCredentialsResponseSchema = z.object({
  credentials: z.array(CredentialSummarySchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

export const DeleteCredentialResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

/**
 * Generic Credentials CRUD Contracts
 */
export const credentialsContracts = {
  create: oc
    .input(CreateCredentialInputSchema)
    .output(CredentialSummarySchema)
    .route({ method: "POST" }),

  update: oc
    .input(UpdateCredentialInputSchema)
    .output(CredentialSummarySchema)
    .route({ method: "POST" }),

  get: oc
    .input(CredentialByIdInputSchema)
    .output(CredentialSummarySchema.nullable())
    .route({ method: "GET" }),

  list: oc
    .input(ListCredentialsInputSchema)
    .output(ListCredentialsResponseSchema)
    .route({ method: "GET" }),

  delete: oc
    .input(CredentialByIdInputSchema)
    .output(DeleteCredentialResponseSchema)
    .route({ method: "DELETE" }),
} as const;

export type CredentialSummaryResponse = z.infer<typeof CredentialSummarySchema>;
export type CreateCredentialInput = z.infer<typeof CreateCredentialInputSchema>;
export type UpdateCredentialInput = z.infer<typeof UpdateCredentialInputSchema>;
export type ListCredentialsInput = z.infer<typeof ListCredentialsInputSchema>;
export type ListCredentialsResponse = z.infer<
  typeof ListCredentialsResponseSchema
>;
